import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { RoutesEnum } from '../../utils/routes';
import { canJumpStepOfSubscription, listAvailablePlansPublic, checkoutPublic, AvailablePlanPublic } from './plan-selection.api';
import { ga4 } from '../../utils/analytics/ga4';

const PlanSelection: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { loginByPassSubscription } = useAuth();
  const [searchParams] = useSearchParams();
  const userAccessGuid = searchParams.get('guid') || '';

  const [loading, setLoading] = useState(false);
  const [checkoutLoadingPriceId, setCheckoutLoadingPriceId] = useState<string | null>(null);
  const [plans, setPlans] = useState<AvailablePlanPublic[]>([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [canJumpSubscriptionStep, setCanJumpSubscriptionStep] = useState(false);
  const [jumpAuthenticationGuid, setJumpAuthenticationGuid] = useState<string | null>(null);
  const [jumpEmail, setJumpEmail] = useState<string | null>(null);
  const [jumpLoading, setJumpLoading] = useState(false);

  const isSinglePlan = plans.length === 1;

  const gridItemProps = useMemo(() => {
    if (isSinglePlan) {
      return { xs: 12, md: 6 };
    }
    return { xs: 12, sm: 6, md: 4 };
  }, [isSinglePlan]);

  useEffect(() => {
    ga4.event('plan_selection_viewed');
  }, []);

  useEffect(() => {
    const fetchPlans = async () => {
      setErrorMessage('');

      if (!userAccessGuid) {
        ga4.event('plan_selection_load_failed', { reason: 'missing_guid' });
        setErrorMessage(t('plan_selection_missing_guid_error'));
        return;
      }

      setLoading(true);
      try {
        const res = await listAvailablePlansPublic(userAccessGuid);
        if (res?.success && Array.isArray(res.data)) {
          setPlans(res.data);
          if (res.data.length === 0) {
            ga4.event('plan_selection_load_failed', { reason: 'no_plans_available' });
            setErrorMessage(t('plan_selection_no_plans_error'));
          }
        } else {
          ga4.event('plan_selection_load_failed', { reason: 'load_response_error' });
          setErrorMessage(res?.message || t('plan_selection_load_error'));
        }
      } catch (err: any) {
        const respData = err?.response?.data;
        const msg = (respData && typeof respData.message === 'string' && respData.message) ? respData.message : '';
        ga4.event('plan_selection_load_failed', { reason: 'load_exception' });
        setErrorMessage(msg || t('plan_selection_load_error'));
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, [t, userAccessGuid]);

  useEffect(() => {
    const emailFromSession = (() => {
      try {
        return sessionStorage.getItem('wg_plan_selection_email');
      } catch {
        return null;
      }
    })();

    if (!emailFromSession) {
      setCanJumpSubscriptionStep(false);
      setJumpAuthenticationGuid(null);
      setJumpEmail(null);
      return;
    }

    setJumpEmail(emailFromSession);

    const checkCanJump = async () => {
      try {
        const res = await canJumpStepOfSubscription(emailFromSession);
        const canJump = Boolean(res?.success && res?.data?.canJumpStepOfSubscription);
        const authGuid = res?.data?.authenticationGuid || null;

        setCanJumpSubscriptionStep(canJump && Boolean(authGuid));
        setJumpAuthenticationGuid(canJump ? authGuid : null);
      } catch {
        setCanJumpSubscriptionStep(false);
        setJumpAuthenticationGuid(null);
      }
    };

    checkCanJump();
  }, []);

  const handleBackToLogin = () => {
    navigate('/login');
  };

  const handleJumpSubscriptionStep = async () => {
    setErrorMessage('');

    if (!jumpEmail || !jumpAuthenticationGuid) {
      ga4.event('plan_selection_jump_failed', { reason: 'missing_context' });
      return;
    }

    setJumpLoading(true);
    try {
      const res = await loginByPassSubscription(jumpEmail, jumpAuthenticationGuid);
      if (res?.success && res?.data?.accessToken) {
        try { sessionStorage.removeItem('wg_plan_selection_email'); } catch {}
        navigate(RoutesEnum.DASHBOARD.HREF(t) as string);
        return;
      }

      ga4.event('plan_selection_jump_failed', { reason: 'bypass_login_failed' });
      setErrorMessage(res?.message || t('plan_selection_load_error'));
    } catch (err: any) {
      const respData = err?.response?.data;
      const msg = (respData && typeof respData.message === 'string' && respData.message) ? respData.message : '';
      ga4.event('plan_selection_jump_failed', { reason: 'bypass_login_exception' });
      setErrorMessage(msg || t('plan_selection_load_error'));
    } finally {
      setJumpLoading(false);
    }
  };

  const handleSubscribeNow = async (priceId: string) => {
    setErrorMessage('');

    if (!userAccessGuid) {
      ga4.event('checkout_failed', { reason: 'missing_guid' });
      setErrorMessage(t('plan_selection_missing_guid_error'));
      return;
    }

    if (!priceId) {
      ga4.event('checkout_failed', { reason: 'missing_price_id' });
      setErrorMessage(t('plan_selection_load_error'));
      return;
    }

    ga4.event('plan_selected', { priceId });
    setCheckoutLoadingPriceId(priceId);
    try {
      ga4.event('checkout_started', { priceId });
      const res = await checkoutPublic(userAccessGuid, priceId);
      if (res?.success && res?.data) {
        const { shouldRedirectToEnrollment, isSuccesToRedirectToCheckout, urlRedirectCheckout } = res.data;
        if (shouldRedirectToEnrollment && !isSuccesToRedirectToCheckout) {
          ga4.event('checkout_failed', { reason: 'requires_enrollment' });
          setErrorMessage(t('subscription_plan_enrollment_warning_billing'));
          return;
        }

        if (!shouldRedirectToEnrollment && isSuccesToRedirectToCheckout && urlRedirectCheckout) {
          window.location.href = urlRedirectCheckout;
          return;
        }
      }

      ga4.event('checkout_failed', { reason: 'checkout_response_error' });
      setErrorMessage(res?.message || t('plan_selection_load_error'));
    } catch (err: any) {
      const respData = err?.response?.data;
      const msg = (respData && typeof respData.message === 'string' && respData.message) ? respData.message : '';
      ga4.event('checkout_failed', { reason: 'checkout_exception' });
      setErrorMessage(msg || t('plan_selection_load_error'));
    } finally {
      setCheckoutLoadingPriceId(null);
    }
  };

  return (
    <Container component="main" maxWidth="md">
      <Box sx={{ mt: 8, mb: 6 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
          <Typography component="h1" variant="h5" color="primary" sx={{ fontWeight: 700 }}>
            {t('plan_selection_title')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {canJumpSubscriptionStep && (
              <Button
                variant="contained"
                onClick={handleJumpSubscriptionStep}
                sx={{ textTransform: 'none' }}
                disabled={jumpLoading}
                startIcon={jumpLoading ? <CircularProgress size={18} color="inherit" /> : undefined}
              >
                {t('plan_selection_skip_subscription_button')}
              </Button>
            )}
            <Button variant="outlined" onClick={handleBackToLogin} sx={{ textTransform: 'none' }}>
              {t('plan_selection_back_to_login')}
            </Button>
          </Box>
        </Box>

        <Typography variant="body1" sx={{ mb: 3 }}>
          {t('plan_selection_subtitle')}
        </Typography>

        {errorMessage && <Alert severity="error" sx={{ mb: 3 }}>{errorMessage}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3} justifyContent={isSinglePlan ? 'center' : 'flex-start'}>
            {plans.map((plan) => (
              <Grid
                key={plan.priceId}
                item
                component={'div' as React.ElementType}
                {...gridItemProps}
              >
                <Card sx={{ height: '100%', position: 'relative', overflow: 'visible' }}>
                  {plan.isProductionPlan === false && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 14,
                        right: 14,
                        px: 1.5,
                        py: 0.5,
                        bgcolor: '#ff9800',
                        color: '#fff',
                        borderRadius: 1,
                        fontSize: '0.65rem',
                        fontWeight: 'bold',
                      }}
                    >
                      Sandbox
                    </Box>
                  )}
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      {plan.commercialName || plan.name}
                    </Typography>
                    {plan.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {plan.description}
                      </Typography>
                    )}
                    {plan.trialConfigurations?.enableTrial && (plan.trialConfigurations.trialDays ?? 0) > 0 && (
                      <Typography variant="body2" color="success.main" sx={{ mb: 2, fontWeight: 600 }}>
                        {t('subscription_plan_trial_days_label', { days: plan.trialConfigurations.trialDays })}
                      </Typography>
                    )}
                    <Typography variant="h5" sx={{ mb: 2 }}>
                      {plan.priceValueNormalized}
                    </Typography>
                    {plan.priceValueNormalizedAsComplementAprox && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2, fontStyle: 'italic' }}>
                        {t('subscription_plan_approximate_conversion_text')} {plan.priceValueNormalizedAsComplementAprox}
                      </Typography>
                    )}
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => handleSubscribeNow(plan.priceId)}
                      disabled={checkoutLoadingPriceId === plan.priceId}
                      startIcon={checkoutLoadingPriceId === plan.priceId ? <CircularProgress size={18} color="inherit" /> : undefined}
                    >
                      {t('subscription_plan_card_subscribe_button')}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Container>
  );
};

export default PlanSelection;
